/**
 * Página de perfil para entrenadores
 * 
 * Muestra:
 * - Información personal del entrenador
 * - Datos de contacto
 * - Configuración de cuenta
 */

import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';

const CoachPerfil = () => {
  const { coachData: _coachData } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estados para los datos del usuario
  const [userData, setUserData] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'coach'
  });

  // Estados para el formulario de edición
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    phone: ''
  });

  // Estados para cambio de contraseña
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Variable responsive
  const isMobile = window.innerWidth <= 768;

  // Cargar datos del usuario al montar el componente
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    fetchUserProfile();
  }, []);

  // Función para cargar el perfil del usuario
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('prokids_token');

      const response = await fetch('/api/users/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const user = await response.json();
        console.log('=== DATOS DEL USUARIO RECIBIDOS ===');
        console.log('Usuario completo:', user);
        console.log('user.name:', JSON.stringify(user.name));
        console.log('user.lastName:', JSON.stringify(user.lastName));
        console.log('user.email:', JSON.stringify(user.email));
        console.log('user.phone:', JSON.stringify(user.phone));
        console.log('===================================');

        const newUserData = {
          name: user.name || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          role: user.role || 'coach'
        };

        const newFormData = {
          name: user.name || '',
          lastName: user.lastName || '',
          phone: user.phone || ''
        };

        console.log('=== ESTABLECIENDO ESTADOS ===');
        console.log('newUserData:', newUserData);
        console.log('newFormData:', newFormData);
        console.log('=============================');

        setUserData(newUserData);
        setFormData(newFormData);
      } else {
        const errorData = await response.json();
        console.error('Error del servidor:', errorData);
        toast.error(errorData.message || 'Error al cargar el perfil');
      }
    } catch (error) {
      console.error('Error al cargar perfil:', error);
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para guardar cambios del perfil
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('prokids_token');

      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Respuesta del servidor:', result);

        if (result.success && result.user) {
          const updatedUser = result.user;
          setUserData(prev => ({
            ...prev,
            name: updatedUser.name || prev.name,
            lastName: updatedUser.lastName || prev.lastName,
            phone: updatedUser.phone || prev.phone
          }));
          setEditMode(false);
          toast.success(result.message || 'Perfil actualizado correctamente');
        } else {
          toast.error(result.message || 'Error al actualizar el perfil');
        }
      } else {
        const errorData = await response.json();
        console.error('Error al actualizar:', errorData);
        toast.error(errorData.message || 'Error al actualizar el perfil');
      }
    } catch (error) {
      console.error('Error al guardar perfil:', error);
      toast.error('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  // Función para cancelar edición
  const handleCancelEdit = () => {
    setFormData({
      name: userData.name,
      lastName: userData.lastName,
      phone: userData.phone
    });
    setEditMode(false);
  };

  // Función para manejar cambio de contraseña
  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword) {
      toast.error('Introduce tu contraseña actual');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('prokids_token');

      const response = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.ok) {
        toast.success('Contraseña cambiada correctamente');
        setShowPasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        const result = await response.json();
        toast.error(result.message || 'Error al cambiar la contraseña');
      }
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      toast.error('Error al cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ textAlign: 'center', color: '#4352A9' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>👤</div>
          <p>Cargando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '20px 16px' : '40px 24px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      
      {/* Header */}
      <div style={{
        marginBottom: isMobile ? '24px' : '32px'
      }}>
        <h1 style={{
          fontSize: isMobile ? '1.8rem' : '2.5rem',
          fontWeight: '700',
          color: '#061156',
          margin: '0 0 8px 0'
        }}>
          👤 Mi Perfil
        </h1>
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          color: '#6c757d',
          margin: 0
        }}>
          Gestiona tu información personal y configuración
        </p>
      </div>

      {/* Información del perfil */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '20px' : '24px',
        marginBottom: isMobile ? '20px' : '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        
        {/* Header de la tarjeta */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e1e5e9'
        }}>
          <h3 style={{
            fontSize: isMobile ? '1.2rem' : '1.4rem',
            fontWeight: '600',
            color: '#061156',
            margin: 0
          }}>
            Información Personal
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {editMode && (
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#ffffff',
                  color: '#6c757d',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: saving ? 0.6 : 1
                }}
              >
                Cancelar
              </button>
            )}
            <button
              onClick={editMode ? handleSaveProfile : () => setEditMode(true)}
              disabled={saving}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #4352A9',
                backgroundColor: editMode ? '#4352A9' : '#ffffff',
                color: editMode ? '#ffffff' : '#4352A9',
                fontSize: '14px',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Guardando...' : (editMode ? 'Guardar' : 'Editar')}
            </button>
          </div>
        </div>

        {/* Campos del perfil */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: isMobile ? '16px' : '20px'
        }}>
          
          {/* Nombre */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#061156',
              marginBottom: '8px'
            }}>
              Nombre
            </label>
            {editMode ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #e1e5e9',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Ej: Vicente"
              />
            ) : (
              <p style={{
                fontSize: '15px',
                color: '#495057',
                margin: 0,
                padding: '12px 0'
              }}>
                {userData.name || 'No especificado'}
              </p>
            )}
          </div>

          {/* Apellidos */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#061156',
              marginBottom: '8px'
            }}>
              Apellidos
              {!userData.lastName && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  color: '#6c757d',
                  marginLeft: '8px'
                }}>
                  (opcional)
                </span>
              )}
            </label>
            {editMode ? (
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #e1e5e9',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Ej: Del Bosque"
              />
            ) : (
              <p style={{
                fontSize: '15px',
                color: userData.lastName ? '#495057' : '#6c757d',
                margin: 0,
                padding: '12px 0',
                fontStyle: userData.lastName ? 'normal' : 'italic'
              }}>
                {userData.lastName || 'Sin apellidos especificados'}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#061156',
              marginBottom: '8px'
            }}>
              Email
            </label>
            <p style={{
              fontSize: '15px',
              color: '#6c757d',
              margin: 0,
              padding: '12px 0',
              fontStyle: 'italic'
            }}>
              {userData.email || 'No especificado'} (no editable)
            </p>
          </div>

          {/* Teléfono */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#061156',
              marginBottom: '8px'
            }}>
              Teléfono
            </label>
            {editMode ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #e1e5e9',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Ej: +34 600 123 456"
              />
            ) : (
              <p style={{
                fontSize: '15px',
                color: '#495057',
                margin: 0,
                padding: '12px 0'
              }}>
                {userData.phone || 'No especificado'}
              </p>
            )}
          </div>

          {/* Rol */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#061156',
              marginBottom: '8px'
            }}>
              Rol
            </label>
            <p style={{
              fontSize: '15px',
              color: '#495057',
              margin: 0,
              padding: '12px 0'
            }}>
              {userData.role === 'coach' ? 'Entrenador' : userData.role}
            </p>
          </div>

        </div>
      </div>

      {/* Configuración de cuenta */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '20px' : '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <h3 style={{
          fontSize: isMobile ? '1.2rem' : '1.4rem',
          fontWeight: '600',
          color: '#061156',
          margin: '0 0 20px 0'
        }}>
          Configuración de Cuenta
        </h3>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          
          <button
            onClick={() => setShowPasswordModal(true)}
            style={{
              padding: '12px 20px',
              borderRadius: '8px',
              border: '1px solid #4352A9',
              backgroundColor: '#ffffff',
              color: '#4352A9',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'left'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(67, 82, 169, 0.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#ffffff';
            }}
          >
            🔒 Cambiar Contraseña
          </button>

          <button
            style={{
              padding: '12px 20px',
              borderRadius: '8px',
              border: '1px solid #6c757d',
              backgroundColor: '#ffffff',
              color: '#6c757d',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'left'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(108, 117, 125, 0.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#ffffff';
            }}
          >
            🔔 Configurar Notificaciones
          </button>

        </div>
      </div>

      {/* Modal para cambiar contraseña */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            width: isMobile ? '90%' : '400px',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '600',
              color: '#061156',
              margin: '0 0 20px 0',
              textAlign: 'center'
            }}>
              🔒 Cambiar Contraseña
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#061156',
                marginBottom: '8px'
              }}>
                Contraseña Actual
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  currentPassword: e.target.value
                }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #e1e5e9',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Tu contraseña actual"
                autoComplete="current-password"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#061156',
                marginBottom: '8px'
              }}>
                Nueva Contraseña
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({
                  ...prev,
                  newPassword: e.target.value
                }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #e1e5e9',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Ingresa tu nueva contraseña"
                autoComplete="new-password"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#061156',
                marginBottom: '8px'
              }}>
                Confirmar Nueva Contraseña
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => {
                  console.log('Cambiando confirmación contraseña:', e.target.value);
                  setPasswordData(prev => ({
                    ...prev,
                    confirmPassword: e.target.value
                  }));
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #e1e5e9',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Confirma tu nueva contraseña"
                autoComplete="new-password"
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: '1px solid #6c757d',
                  backgroundColor: '#ffffff',
                  color: '#6c757d',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: saving ? 0.6 : 1
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handlePasswordChange}
                disabled={saving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: '1px solid #4352A9',
                  backgroundColor: '#4352A9',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (saving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: (saving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) ? 0.6 : 1
                }}
              >
                {saving ? 'Cambiando...' : 'Cambiar Contraseña'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CoachPerfil;
